/**
 * Message Parser Service
 * Extracts structured data from AI agent responses
 */
import { mergeCategoriesWithPresets } from "../core/constants/categories";
/**
 * Message Parser Service
 * Handles parsing of AI agent responses and function calls/responses
 */
export class MessageParser {
    /**
     * Parse raw AI response data into structured format
     */
    parseMessageData(rawData) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        let offers = [];
        let brands = [];
        let categories = [];
        let searchCategories = [];
        let products = [];
        let showWaysToEarn = false;
        // New format: function_response at top level
        const functionResponse = rawData.function_response ||
            ((_c = (_b = (_a = rawData.content) === null || _a === void 0 ? void 0 : _a.parts) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.functionResponse);
        const functionCall = (_f = (_e = (_d = rawData.content) === null || _d === void 0 ? void 0 : _d.parts) === null || _e === void 0 ? void 0 : _e[0]) === null || _f === void 0 ? void 0 : _f.functionCall;
        if (functionResponse) {
            console.log("[MessageParser] Function response detected:", functionResponse.name);
            if (functionResponse.name === "query_offers") {
                const matches = ((_g = functionResponse.response) === null || _g === void 0 ? void 0 : _g.matches) || [];
                console.log("[MessageParser] Parsing offers, count:", matches.length);
                offers = this.parseOffers(matches);
                console.log("[MessageParser] Parsed offers:", offers.length);
            }
            else if (functionResponse.name === "get_signup_earning_brands") {
                const rawBrands = ((_h = functionResponse.response) === null || _h === void 0 ? void 0 : _h.brands) || [];
                brands = this.parseBrands(rawBrands);
            }
            else if (functionResponse.name === "get_category_purchase_earning") {
                const rawCategories = ((_j = functionResponse.response) === null || _j === void 0 ? void 0 : _j.categories) || [];
                categories = mergeCategoriesWithPresets(rawCategories);
            }
            else if (functionResponse.name === "get_categories") {
                const rawCategories = ((_k = functionResponse.response) === null || _k === void 0 ? void 0 : _k.categories) || [];
                console.log("[MessageParser] Parsing search categories, count:", rawCategories.length);
                searchCategories = this.parseSearchCategories(rawCategories);
                console.log("[MessageParser] Parsed search categories:", searchCategories.length);
            }
            else if (functionResponse.name === "ways_to_earn") {
                // New format: ways_to_earn is now a function_response
                console.log("[MessageParser] Ways to earn detected");
                showWaysToEarn = true;
            }
            else if (functionResponse.name === "query_products") {
                const matches = ((_l = functionResponse.response) === null || _l === void 0 ? void 0 : _l.matches) || [];
                console.log("[MessageParser] Parsing products, count:", matches.length);
                products = this.parseProducts(matches);
                console.log("[MessageParser] Parsed products:", products.length);
            }
        }
        else if ((functionCall === null || functionCall === void 0 ? void 0 : functionCall.name) === "ways_to_earn") {
            // Old format: ways_to_earn as function_call (keep for backwards compatibility)
            showWaysToEarn = true;
        }
        return { offers, brands, categories, searchCategories, products, showWaysToEarn };
    }
    /**
     * Parse offers from query_offers function response
     * New format:
     * [0] = id, [1] = name, [2] = offerCode, [3] = price, [4] = description,
     * [5] = available methods, [6] = discountType, [7] = discountDetails array,
     * [8] = variant title, [9] = null, [10] = null, [11] = brandName, [12] = image
     */
    parseOffers(matches) {
        return matches.map((match) => ({
            id: match[0] || "",
            name: match[1] || "Unnamed Offer",
            offerCode: match[2] || "",
            price: match[3] || 0,
            description: match[4] || "",
            discountType: match[6] || "",
            discountDetails: match[7] || [],
            brandName: match[11] || "Unknown Brand",
            image: match[12] || undefined,
        }));
    }
    /**
     * Parse brands from get_signup_earning_brands function response
     */
    parseBrands(brands) {
        return brands.map((brand) => ({
            id: brand.id || "",
            name: brand.name || "Unknown Brand",
            logoUrl: brand.logoUrl || null,
            description: brand.description || null,
            websiteUrl: brand.websiteUrl || null,
            shopifyStoreUrl: brand.shopifyStoreUrl || null,
            network: brand.network || "sepolia",
            categoryId: brand.categoryId || "",
            categoryName: brand.categoryName || "Unknown Category",
            rewardDetails: brand.rewardDetails || {
                earningMethodId: "",
                earningType: "sign_up",
                isActive: true,
                rewardExistingCustomers: false,
                rewardInfo: {
                    id: "",
                    rewardName: "",
                    rewardSymbol: "",
                    rewardImage: "",
                    rewardValueInDollars: "0",
                    rewardOriginalValue: "0",
                },
                rules: [],
            },
        }));
    }
    /**
     * Parse search categories from get_categories function response
     */
    parseSearchCategories(rawCategories) {
        return rawCategories.map((cat) => {
            var _a;
            return ({
                categoryId: cat.categoryId || "",
                categoryName: cat.categoryName || "Unknown Category",
                title: cat.categoryName || "Unknown Category",
                brandCount: cat.brandCount || 0,
                image: cat.categoryImage ||
                    `https://via.placeholder.com/300x200?text=${((_a = cat.categoryName) === null || _a === void 0 ? void 0 : _a.charAt(0)) || "C"}`,
                description: cat.categoryDescription || undefined,
                categorySlug: cat.categorySlug || undefined,
                categoryType: cat.categoryType || undefined,
                categoryBanner: cat.categoryBanner || undefined,
            });
        });
    }
    /**
     * Parse products from query_products function response
     */
    parseProducts(matches) {
        return matches.map((product) => ({
            product_id: product.product_id || "",
            product_name: product.product_name || "Unnamed Product",
            brand_name: product.brand_name || "Unknown Brand",
            description: product.description || "",
            price: product.price || 0,
            discounts: product.discounts || [],
            brand_shopify_url: product.brand_shopify_url || "",
            productUrl: product.productUrl || "",
            coverImage: product.coverImage || "",
        }));
    }
}
